import { createSelector } from 'reselect';
import { RootState } from 'typesafe-actions';
import { ReducerPayloadAction } from '@okapi/router-connect';
import trimEnd from 'lodash.trimend';
import { queryString } from 'utils/query-string';
import { patchUrlByLocale, range, getDomain, extractIdFromPathname } from 'utils';

export const DEFAULT_RU_REGION = 'moscow';
export const DEFAULT_KZ_REGION = 'kazakhstan';

export const RU_DOMAINS = [
  'adeo.com',
  'leroymerlin.ru',
  'localhost',
  'lmru.tech',
  'lemonlid.ru',
  'lemanapro.ru',
];
export const KZ_DOMAINS = ['lemanapro.kz', 'lmkz.tech', 'leroymerlin.kz'];

const isDomainValid = (domain: string, domains: string[]) =>
  domains.some((matchingDomain) => domain === matchingDomain);

export const getSearch = createSelector(
  ({ router }: RootState) => router?.search,
  (search) => search,
);

export const getQuery = createSelector(getSearch, (search) => queryString.parse(search));

export const getPathname = createSelector(
  ({ router }: RootState) => router?.path,
  (s) => s,
);
export const getRouterHash = createSelector(
  ({ router }: RootState) => router.hash,
  ({ router }: { router: ReducerPayloadAction }) => router.path,
  (s) => s,
);
export const getHost = ({ host }: { host: string }) => host;
export const getArticle = createSelector(getPathname, (pathname) => extractIdFromPathname(pathname));

export const getIsShortProductPath = createSelector(getPathname, getArticle, (pathname, article) => {
  return trimEnd(pathname, '/') === `/product/-${article}`;
});

export const getIsSetPath = createSelector(getPathname, (pathname) => {
  return pathname.startsWith('/set/');
});

export const getHostname = createSelector(getHost, (host) => host?.replace(/:[0-9]+/, ''));

export const getBaseUrl = createSelector(
  getHostname,
  getPathname,
  (host, pathname) => `https://${host}${patchUrlByLocale(pathname)}`,
);

export const getDomainSelector = createSelector(getHost, (hostname) => {
  return getDomain(hostname);
});

/**
 * @deprecated Use `getRegionCode`
 */
export const getRegion = createSelector(getHostname, getDomainSelector, (hostname, domain) => {
  const allFsStands = range(20).map((index) => `fs${index}`);
  const definitelyNotARegion = [
    ...allFsStands,
    'aem',
    'arulm',
    'author',
    'dispatcher',
    'internal',
    'isomorphic-shared-stage',
    'lemanapro',
    'lemonlid',
    'leroymerlin',
    'localhost',
    'mf',
    'mf-capella-a',
    'mf-capella-a-stage',
    'mf-taurus-a',
    'publish',
    'ux',
    'yandex',
    'triffle',
  ];

  const isRuDomain = isDomainValid(domain, RU_DOMAINS);
  const isKZDomain = isDomainValid(domain, KZ_DOMAINS);

  if (isRuDomain || isKZDomain) {
    const [maybeRegion] = hostname.split('.');

    if (!definitelyNotARegion.some((prefix) => maybeRegion.startsWith(prefix))) {
      return maybeRegion;
    }
  }

  if (isRuDomain) {
    return DEFAULT_RU_REGION;
  }

  if (isKZDomain) {
    return DEFAULT_KZ_REGION;
  }

  return '';
});

export const getRegionCode = getRegion;
